import { Fragment, useEffect, useState, useRef } from 'react';
import { gtmPushEvent } from '@Components/Utilities/SandhillsUtility';
import UserSettingSelector from '@Components/Shared/UserSettingSelector';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { modalType, gtmEventType } from '@Components/Utilities/SandhillsConstants';
import { useSetRecoilState, useRecoilValue } from 'recoil';
import Paper from '@mui/material/Paper';
import Popover from '@mui/material/Popover';
import Badge from '@mui/material/Badge';
import MailIcon from '@mui/icons-material/Mail';
import Box from '@mui/material/Box';
import { useWindowSize } from '@Components/Utilities/WindowSize';
import translator from '@Components/Utilities/Translator';
import { loadable } from '@Components/Utilities/SandhillsLoadable';

const QuoteMessageAlert = loadable(() => import('@Components/Quotes/QuoteMessageAlert'));
const TextSignUpModal = loadable(() => import('@Components/Shared/Modals/TextSignUpModal'));

const HeaderLink = (props) => {
    const windowSize = useWindowSize();
    const [anchorEl, setAnchorEl] = useState(null);
    const anchorRef = useRef(null);
    const isPopoverOpen = Boolean(anchorEl);
    const setShowQsuModal = useSetRecoilState(atoms.showQsu);
    const setShowRaqModal = useSetRecoilState(atoms.showRaq);
    const currentSite = useRecoilValue(atoms.currentWebsite);
    const [categoryLinksVisible, setCategoryLinksVisible] = useState(false);
    const [autoOverflow, setAutoOverflow] = useState(true);
    const isComponentSubMenu = Boolean(props.SubMenu?.ReactComponentName);
    const hasSubMenuLinks = props.SubMenu?.Links?.length > 0;
    const showMoreButton = !props.SubMenu || isComponentSubMenu || hasSubMenuLinks;
    const [showTsuModal, setShowTsuModal] = useState(false);

    useEffect(() => {
        if (props.OpenModalOnLoad) {
            tryOpenModal(props, 'onLoad');
        } else {
            props.SubMenu?.Links?.forEach(link => {
                if (link.OpenModalOnLoad) {
                    tryOpenModal(link, 'onLoad');
                }
            });
        }
    }, []);

    const tryOpenModal = (link, actionType) => {
        if (link.LinkModalType == modalType.qsuModal) {
            setShowQsuModal(true);
            gtmPushEvent('quick-sign-up-open', 'qsuTriggerType', actionType);
        } else if (link.LinkModalType == modalType.tsuModal) {
            setShowTsuModal(true);
        } else if (link.LinkModalType == modalType.raqModal) {
            setShowRaqModal(true);
        }
    };

    const headerLinkClick = (link, hasSubmenu) => {
        if (link.GTMEventType) {
            gtmPushEvent(link.GTMEventType);
        }

        if (link?.ClassName == 'logout-link') {
            localStorage?.removeItem('iterableSubscriptionId'); 
            localStorage?.removeItem('pushSubscriptionLastUpdate');
        }

        if (hasSubmenu) {
            if (props.activeSubMenu != props.SubMenu.ReactComponentName) {
                props.onSetActiveSubMenu(props.SubMenu.ReactComponentName);
            } else {
                props.onSetActiveSubMenu('');
            }
        } else {
            tryOpenModal(link, 'Click');
        }
    };

    const handlePopoverClick = () => {
        setAnchorEl(anchorRef.current);
    };

    const handlePopoverClose = () => {
        setAnchorEl(null);
    };

    const handleATPopoverOpen = () => {
        setCategoryLinksVisible(true);
    }

    const handleATPopoverClose = () => {
        setCategoryLinksVisible(false);
    }

    const handleATPopoverClick = () => {
        setAutoOverflow(!autoOverflow)
    }

    const getSubMenuComponent = (compName, compProps) => {
        if (compName === 'UserSettingSelector') {
            return (
                <UserSettingSelector key={`${props.Key}_${compName}`} {...compProps.UserSettingSelector} />
            );
        } else if (compName === 'QuoteMessageAlert') {
            return (
                <QuoteMessageAlert key={`${props.Key}_${compName}`} {...compProps.QuoteMessageAlert} />
            );
        }

        return null;
    };

    const getSubMenuLinkIndex = () => {
        let index = props.SubMenu.Links.findIndex(lnk => lnk.Text === "Sign Up For Text Messages");
        return index;
    };

    const renderBasicLink = (hasSubmenu, showArrow) => {
        return (
            <>
                {props.Url ?
                    <a
                        href={props.Url}
                        target={props.TargetName ? props.TargetName : null}
                        className={`header-submenu__link ${(props.ClassName ? props.ClassName : '')}`}
                        aria-label={(props.AriaLabel ? props.AriaLabel : props.Text)}
                        onMouseEnter={hasSubmenu ? () => props.onSetActiveSubMenu(props.SubMenu.ReactComponentName) : null}
                        onMouseLeave={(hasSubmenu && props.SubMenu.ReactComponentName != 'UserSettingSelector') ? () => props.onSetActiveSubMenu('') : null}
                        onClick={() => headerLinkClick(props, hasSubmenu)}
                        rel={props.Relation ? props.Relation : null}
                    >
                        
                        {!windowSize.isMobile && props.UseBadge &&
                            <Box sx={{ fontSize: '13px', display: 'inline', marginRight: '7px', '& .MuiBadge-root': { verticalAlign: 'sub', display: 'inline-block' }, '& .MuiSvgIcon-root': { fontSize: '1.1rem' } }}>
                                <Badge badgeContent={props.QuoteMessageAlert?.MessagesCount ?? 0} color="action">
                                    <MailIcon color="secondary" fontSize="small" />
                                </Badge>
                            </Box>
                        }
                        {props.Icon &&
                            <>
                            {props.IconIsImage ? <img src={props.Icon} alt={props.IconImageAltText} /> : <i className={`fas ${props.Icon}`} />}
                                &nbsp;
                            </>}
                        {windowSize.isMobile && props.UseBadge ?
                            <Box sx={{ display: 'inline-block', alignItems: 'center' }}>
                                <Badge badgeContent={props.QuoteMessageAlert?.MessagesCount ?? 0} color="primary">
                                    <MailIcon color="black" fontSize="medium" />
                                </Badge>
                                <div className='text'>{props.Text}</div>
                            </Box>

                            : props.Text}
                        {showArrow && <i className='sub-menu-arrow fas fa-caret-down' />}
                    </a>
                    :
                    <a role="button"
                        tabIndex="0"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                headerLinkClick(props);
                            }
                        }}
                        target={props.TargetName ? props.TargetName : null}
                        className={"header-submenu__link " + (props.ClassName ? props.ClassName : '')}
                        aria-label={(props.AriaLabel ? props.AriaLabel : props.Text)}
                        onClick={() => headerLinkClick(props)}
                        onMouseEnter={hasSubmenu ? () => props.onSetActiveSubMenu(props.SubMenu.ReactComponentName) : null}
                        onMouseLeave={(hasSubmenu && props.SubMenu.ReactComponentName != 'UserSettingSelector') ? () => props.onSetActiveSubMenu('') : null}
                        rel={props.Relation ? props.Relation : null}>
                        {props.Icon &&
                            <>
                            {props.IconIsImage ? <img src={props.Icon} alt="" /> : <i className={`fas ${props.Icon}`} />}
                                &nbsp;
                            </>}
                        {props.Text}
                        {showArrow && <i className='sub-menu-arrow fas fa-caret-down' />}
                    </a>
                }
            </>
        );
    };

    const featuredLinksForAT = () => {
        return (
            <>
                {!props.SubMenu ?
                    renderBasicLink(false)
                    :
                    <div className={props.UseBadge && windowSize.isMobile ? 'messages-header-button-div' : `shp-container ${props.ClassName || ''}`}
                        onClick={handleATPopoverClick}
                        onMouseEnter={handleATPopoverOpen}
                        onMouseLeave={handleATPopoverClose}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                if (e.target.getAttribute('aria-expanded') === 'true') {
                                    handleATPopoverClose();
                                } else {
                                    handleATPopoverOpen();
                                }
                            }
                        }}
                    >
                        <button
                            ref={anchorRef}
                            className={props.UseBadge && windowSize.isMobile ? 'messages-header-button' : 'shp__trigger ts-button'}
                            aria-label={props.AriaLabel ? `${props.AriaLabel} - ${props.Text}` : props.Text}
                            aria-haspopup='true'
                            aria-expanded={categoryLinksVisible}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    if (e.target.getAttribute('aria-expanded') === 'true') {
                                        handleATPopoverClose();
                                    } else {
                                        handleATPopoverOpen();
                                    }
                                }
                            }}
                        >
                            {props.Icon && props.ShowIconOnLeft && (
                                <span className='shp__trigger-icon'>
                                    {props.IconIsImage ? (
                                        <img src={props.Icon} alt='' />
                                    ) : (
                                        <i className={`fas ${props.Icon}`} />
                                    )}
                                    &nbsp;
                                </span>                                
                            )}
                            {!windowSize.isMobile && props.UseBadge && (
                                <Box sx={{ fontSize: '13px', display: 'inline', marginRight: '7px', '& .MuiBadge-root': { verticalAlign: 'sub', display: 'inline-block' }, '& .MuiSvgIcon-root': { fontSize: '1.1rem' } }}>
                                    <Badge badgeContent={props.QuoteMessageAlert?.MessagesCount ?? 0} color="action">
                                        <MailIcon color="secondary" fontSize="small" />
                                    </Badge>
                                </Box>
                            )}
                            {windowSize.isMobile && props.UseBadge ? (
                                <Box sx={{ fontSize: '11px', textTransform: 'uppercase', display: 'block' }}>
                                    <Badge badgeContent={props.QuoteMessageAlert?.MessagesCount ?? 0} color="primary">
                                        <MailIcon color="black" fontSize="medium" />
                                    </Badge>
                                    {props.Text}
                                </Box>
                            ) : (
                                props.Text
                            )}
                            {props.Icon && !props.ShowIconOnLeft &&  (
                                <span className='shp__trigger-icon'>
                                    {props.IconIsImage ? (
                                        <img src={props.Icon} alt='' />
                                    ) : (
                                        <i className={`fas ${props.Icon}`} />
                                    )}
                                    &nbsp;
                                </span>
                            )}
                            <span className='shp__trigger-arrow'>
                                {props.SubMenu?.ShowArrow && (
                                    <i className={`fas ${isPopoverOpen ? 'fa-caret-up' : 'fa-caret-down'}`} />
                                )}
                            </span>
                        </button>
                        {props.isLangSelector ?
                            <div className='submenu-div'>
                                <ul className='submenu-links-container' style={{ display: categoryLinksVisible ? 'block' : 'none' }} >
                                    <li>
                                        {getSubMenuComponent(props.SubMenu.ReactComponentName, props.SubMenu.ReactComponentProps)}
                                    </li>
                                </ul>
                            </div>
                            
                            :
                            <div className='submenu-div' >

                                <ul className='submenu-links-container' style={{ display: categoryLinksVisible ? 'block' : 'none' }}>
                                    {hasSubMenuLinks && props.SubMenu?.Links?.filter(lnk => props.ShowOnDesktop).map(sublnk => (
                                        <li key={sublnk.key}
                                            className={`header-submenu__list-item ${(sublnk.ApplyStylingToParent ? sublnk.ClassName : '')}`}
                                        >
                                            {sublnk.SubMenu ? (
                                                <>
                                                    <div className='attachment-submenu' >
                                                        <a
                                                            href={sublnk.Url}
                                                            target={sublnk.TargetName ? sublnk.TargetName : null}
                                                            className={`header-submenu__link ${(sublnk.ClassName ? sublnk.ClassName : '')}`}
                                                            aria-label={sublnk.AriaLabel ? sublnk.AriaLabel : sublnk.Text}
                                                            onClick={() => {
                                                                headerLinkClick(sublnk);
                                                            }}
                                                            rel={sublnk.Relation ? sublnk.Relation : null}
                                                        >
                                                            {sublnk.Text}

                                                            {sublnk.Icon ? (
                                                                <span className='shp__trigger-icon'>
                                                                    {sublnk.IconIsImage ?
                                                                        <img src={sublnk.Icon} alt='' /> :
                                                                        <i className={`fas ${sublnk.Icon}`} />}
                                                                </span>
                                                            ) : (
                                                                <span className='shp__trigger-arrow'>
                                                                    <i className='fas fa-caret-right' />
                                                                </span>
                                                            )}
                                                            
                                                        </a>
                                                        
                                                        <ul className='submenu-links-container'>
                                                            {sublnk.SubMenu?.Links?.map(attachmentLnk => (

                                                                <li key={attachmentLnk.key} className={`header-submenu__list-item ${(attachmentLnk.ApplyStylingToParent ? attachmentLnk.ClassName : '')}`}>
                                                                    <a
                                                                        role="button"
                                                                        tabIndex="0"
                                                                        href={attachmentLnk.Url}
                                                                        target={attachmentLnk.TargetName ? attachmentLnk.TargetName : null}
                                                                        className={`header-submenu__link ${(attachmentLnk.ClassName ? attachmentLnk.ClassName : '')}`}
                                                                        aria-label={attachmentLnk.AriaLabel ? attachmentLnk.AriaLabel : attachmentLnk.Text}
                                                                        onClick={() => {
                                                                            headerLinkClick(attachmentLnk);
                                                                        }}
                                                                        onKeyDown={(e) => {
                                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                                headerLinkClick(attachmentLnk);
                                                                                handleATPopoverClose();
                                                                            } 
                                                                        }}

                                                                        rel={attachmentLnk.Relation ? attachmentLnk.Relation : null}
                                                                    >
                                                                        {attachmentLnk.Text}
                                                                    </a>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                </>

                                            ) : (
                                                    <>
                                                        {sublnk.Icon && sublnk.ShowIconOnLeft &&
                                                            <span className='shp__trigger-icon'>
                                                                {sublnk.IconIsImage ?
                                                                    <img src={sublnk.Icon} alt='' /> :
                                                                    <i className={`fas ${sublnk.Icon}`} />}
                                                            </span>
                                                        }
                                                        <a
                                                            {...(sublnk.Url ? { href: sublnk.Url } : {})}
                                                            role="button"
                                                            tabIndex="0"
                                                            target={sublnk.TargetName ? sublnk.TargetName : null}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter' || e.key === ' ') {
                                                                    headerLinkClick(sublnk);
                                                                    handleATPopoverClose();
                                                                }
                                                            }}
                                                        className={`header-submenu__link ${(sublnk.ClassName ? sublnk.ClassName : '')}`}
                                                        aria-label={sublnk.AriaLabel ? sublnk.AriaLabel : sublnk.Text}
                                                        onClick={() => {
                                                            headerLinkClick(sublnk);
                                                        }}
                                                        rel={sublnk.Relation ? sublnk.Relation : null}
                                                    >
                                                        {sublnk.Text}
                                               
                                                    </a>
                                                
                                                    {sublnk.Icon && !sublnk.ShowIconOnLeft &&
                                                        <span className='shp__trigger-icon'>
                                                            {sublnk.IconIsImage ?
                                                                <img src={sublnk.Icon} alt='' /> :
                                                                <i className={`fas ${sublnk.Icon}`} />}
                                                        </span>
                                                    }
                                                </>
                                            )}                                            
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        }
                    </div>
                }
            </>
        );
    };

    return (
        <>
        <Fragment key={props.Key}>
            {currentSite.Site.IsAuctionTime && !windowSize.isMobile ?
                featuredLinksForAT() 
                :
                <>
            
                    {!props.SubMenu ?
                        renderBasicLink(false) :
                        showMoreButton &&
                        <>
                            {props.IsHeaderAdvancedView ?
                                renderBasicLink(true, props.SubMenu.ShowArrow) :
                                <div className={props.UseBadge && windowSize.isMobile ? 'messages-header-button-div' : `shp-container ${(props.ClassName ? props.ClassName : '')}`}>
                                    <button
                                            ref={anchorRef}
                                            className={props.UseBadge && windowSize.isMobile ? 'messages-header-button' : 'shp__trigger ts-button'}
                                            aria-label={props.AriaLabel ? props.AriaLabel + ' - ' + props.Text : props.Text}
                                            aria-haspopup='true'
                                            aria-expanded={isPopoverOpen ? true : false}
                                            onClick={() => { handlePopoverClick(); gtmPushEvent(gtmEventType.moreExpanded); }}
                                    >
                                        {!windowSize.isMobile && props.UseBadge &&
                                            <Box sx={{ fontSize: '13px', display: 'inline', marginRight: '7px', '& .MuiBadge-root': { verticalAlign: 'sub', display: 'inline-block' }, '& .MuiSvgIcon-root': { fontSize: '1.1rem'} }}>
                                                <Badge badgeContent={props.QuoteMessageAlert?.MessagesCount ?? 0} color="action">
                                                    <MailIcon color="secondary" fontSize="small" />
                                                </Badge>
                                            </Box>
                                        }
                                        {windowSize.isMobile && props.UseBadge ?
                                            <Box sx={{ fontSize: '11px', textTransform: 'uppercase', display: 'block' }}>
                                                <Badge badgeContent={props.QuoteMessageAlert?.MessagesCount ?? 0} color="primary">
                                                    <MailIcon color="black" fontSize="medium" />
                                                </Badge>
                                                {props.Text}
                                            </Box>

                                            : props.Text}
                                        {props.Icon &&
                                            <span className='shp__trigger-icon'>
                                                {props.IconIsImage ?
                                                    <img src={props.Icon} alt="" /> :
                                                    <i className={`fas ${props.Icon}`} />}
                                            </span>}
                                        <span className='shp__trigger-arrow'>
                                            {props.SubMenu.ShowArrow &&
                                                <i className={`fas ${(isPopoverOpen ? 'fa-caret-up' : 'fa-caret-down')}`} />}
                                        </span>
                                    </button>
                            <Popover
                                open={isPopoverOpen}
                                anchorEl={anchorEl}
                                onClose={handlePopoverClose}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: (props.UseLeftAlignedPopover || currentSite.Site.IsAuctionTime) ? 'left' : 'center',
                                }}
                                max-width={props.UseBadge ? '350px' : '100%' }
                            >
                                <Paper className='shp-content' elevation={3}>
                                    {props.SubMenu.ReactComponentName ?
                                        getSubMenuComponent(props.SubMenu.ReactComponentName, props.SubMenu.ReactComponentProps) :
                                        <ul className='header-submenu'>
                                            {props.SubMenu.Links.filter(lnk => props.ShowOnDesktop).map(sublnk =>
                                                <li key={sublnk.Key} className={`header-submenu__list-item ${(sublnk.ApplyStylingToParent ? sublnk.ClassName : '')}`}>
                                                    {sublnk.Url ?
                                                        <>
                                                            {sublnk.Icon && sublnk.ShowIconOnLeft &&
                                                                <span className='shp__trigger-icon'>
                                                                    {sublnk.IconIsImage ?
                                                                        <img src={sublnk.Icon} alt="" /> :
                                                                        <i className={`fas ${sublnk.Icon}`} />}
                                                                    &nbsp;
                                                                </span>}
                                                            <a
                                                                href={sublnk.Url}
                                                                target={sublnk.TargetName ? sublnk.TargetName : null}
                                                                className={`header-submenu__link ${(sublnk.ClassName ? sublnk.ClassName : '')}`}
                                                                aria-label={(sublnk.AriaLabel ? sublnk.AriaLabel : sublnk.Text)}
                                                                onClick={() => {
                                                                    headerLinkClick(sublnk);
                                                                    handlePopoverClose();
                                                                }}
                                                                rel={sublnk.Relation ? sublnk.Relation : null}
                                                            >
                                                                {sublnk.Text}
                                                            </a>
                                                            {sublnk.Icon && !sublnk.ShowIconOnLeft &&
                                                                <span className='shp__trigger-icon'>
                                                                    {sublnk.IconIsImage ?
                                                                        <img src={sublnk.Icon} alt="" /> :
                                                                        <i className={`fas ${sublnk.Icon}`} />}
                                                                </span>}</>
                                                        :
                                                        <a
                                                            role="button"
                                                            tabIndex="0"
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter' || e.key === ' ') {
                                                                    e.preventDefault();
                                                                    headerLinkClick(sublnk);
                                                                    handlePopoverClose();

                                                                        }
                                                                    }}
                                                                    target={sublnk.TargetName ? sublnk.TargetName : null}
                                                                    className={`header-submenu__link ${(sublnk.ClassName ? sublnk.ClassName : '')}`}
                                                                    aria-label={sublnk.AriaLabel ? sublnk.AriaLabel : sublnk.Text}
                                                                    onClick={() => {
                                                                        headerLinkClick(sublnk);
                                                                        handlePopoverClose();
                                                                    }}
                                                                    rel={sublnk.Relation ? sublnk.Relation : null}
                                                                >
                                                                    {sublnk.Text}
                                                                </a>
                                                            }
                                                        </li>)}
                                                </ul>}
                                        </Paper>
                                    </Popover>
                                </div>}
                        </>}
                </>}
        </Fragment>
            {!windowSize.isMobile && showTsuModal && <TextSignUpModal {...props.SubMenu.Links[getSubMenuLinkIndex()].TextSignUpModel} setShowTsuModal={setShowTsuModal} /> }
        </>
    );
};

export default HeaderLink;